import { useContext } from 'react'

import { actionTypes, allowedExchangeMethods } from '@/constants'
import AppContext from '@/context/AppContext'

import useTransactionTypes from '../useTransactionTypes/useTransactionTypes'

const useItemTransactionType = () => {
  const { context } = useContext(AppContext)
  const { allowsUnifiedFlow, transactionTypeSelectedByUser } = useTransactionTypes()

  const showTransactionTypeSelector = item => {
    if (allowsUnifiedFlow && item?.available_transaction_types?.length > 1) {
      return isOnlyVariantsExchangeAllowed(item) ? itemHasVariants(item) : true
    }

    return false
  }

  const getItemDefaultTransactionType = item => {
    if (allowsUnifiedFlow) {
      if (item?.available_transaction_types?.length === 1) return item.available_transaction_types[0]

      if (showTransactionTypeSelector(item)) {
        return null
      } else {
        return actionTypes.refund
      }
    }

    return transactionTypeSelectedByUser
  }

  const itemHasVariants = item => {
    const originalId = item.id.replace(/-.*/, '')

    return context.variants?.[originalId]?.some(variant => variant.stock_quantity > 0)
  }

  const isOnlyVariantsExchangeAllowed = item => {
    return (
      item.available_exchange_methods?.length === 1 &&
      item.available_exchange_methods[0] === allowedExchangeMethods.variants
    )
  }

  const itemOnlySupportsExchange = item => {
    return item.available_transaction_types.length === 1 && item.available_transaction_types[0] === actionTypes.exchange
  }

  const isItemReturnable = item => {
    if (allowsUnifiedFlow) {
      if (itemOnlySupportsExchange(item) && isOnlyVariantsExchangeAllowed(item)) {
        return itemHasVariants(item)
      }
      return item.available_transaction_types.length > 0
    }

    if (item.available_transaction_types.includes(transactionTypeSelectedByUser)) {
      if (transactionTypeSelectedByUser === actionTypes.exchange && isOnlyVariantsExchangeAllowed(item)) {
        return itemHasVariants(item)
      }

      return true
    }

    return false
  }

  const getItemNoReturnableReason = item => {
    if (allowsUnifiedFlow) {
      if (
        itemOnlySupportsExchange(item) &&
        isOnlyVariantsExchangeAllowed(item) &&
        !itemHasVariants(item) &&
        !item.is_returnable_reason
      ) {
        return 'no_variants'
      }
    }

    if (
      item.available_transaction_types.includes(transactionTypeSelectedByUser) &&
      isOnlyVariantsExchangeAllowed(item) &&
      !itemHasVariants(item) &&
      !item.is_returnable_reason
    ) {
      return 'no_variants'
    }

    return item.is_returnable_reason
  }

  const isItemForExchange = item => {
    return item.exchanged_for_item || item.exchange_info
  }

  return {
    isItemReturnable,
    getItemNoReturnableReason,
    getItemDefaultTransactionType,
    showTransactionTypeSelector,
    itemHasVariants,
    isOnlyVariantsExchangeAllowed,
    itemOnlySupportsExchange,
    isItemForExchange,
  }
}

export default useItemTransactionType
